import React, { useState, useEffect, useRef } from 'react';

interface VisualizerProps {
  className?: string;
  barCount?: number;
}

const Visualizer: React.FC<VisualizerProps> = ({ 
  className = '',
  barCount = 10
}) => {
  const [isActive, setIsActive] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setIsActive(prev => !prev);
    }, 8000); // Reduced frequency from 5000ms to 8000ms
    
    return () => clearInterval(interval);
  }, [isVisible]);
  
  if (!isVisible) return null;

  return (
    <div ref={containerRef} className={`flex items-center justify-center h-12 ${className}`}>
      {[...Array(barCount)].map((_, i) => {
        // Create varying heights and animations for each bar
        const height = 10 + Math.random() * 50; // Between 10px and 60px
        const duration = (0.8 + Math.random() * 1.2).toFixed(1); // Slower animations
        const delay = (Math.random() * 0.5).toFixed(1); // Between 0s and 0.5s
        
        return (
          <div 
            key={i}
            className="visualizer-bar"
            style={{
              height: `${height}px`,
              animationDuration: isActive ? `${duration}s` : '0s',
              animationDelay: `${delay}s`,
              opacity: isActive ? 1 : 0.6,
              transition: 'opacity 1.5s ease' // Slower transitions
            }}
          />
        );
      })}
    </div>
  );
};

export default Visualizer;
